import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Label } from 'components/ui/label';
import { MultiSelect } from 'components/ui/multi-select';
import { useSpecialties, useExpertisesForSpecialties } from 'hooks/useReferentials';
import { controlId, errorId, validationMessage, type FormErrors } from './validation';
import type { Expertise } from 'api/referentialsApi';

interface SkillsSectionProps {
  specialtyIds: number[];
  expertiseIds: number[];
  onChange: (patch: { specialtyIds?: number[]; expertiseIds?: number[] }) => void;
  readOnly: boolean;
  /** Blocking on a new record only: the API has never required a specialty. */
  creating?: boolean;
  errors?: FormErrors;
}

/**
 * Specialties and the expertises hanging under them. The offered expertises come from the L1b
 * cascade (`GET /expertises?specialtyIds=…`), which returns active values only; the specialties
 * listing supplies the catalogue used to keep naming an expertise the record already carries
 * after it was deactivated.
 */
const SkillsSection = ({ specialtyIds, expertiseIds, onChange, readOnly, creating = false, errors = {} }: SkillsSectionProps) => {
  const { t } = useTranslation();
  const { data: specialties } = useSpecialties();
  const { data: cascade } = useExpertisesForSpecialties(specialtyIds);

  // Every expertise in the referential, deduplicated: a shared one appears under each of its
  // specialties. It carries `specialty_ids`, which is what the pruning below reads.
  const catalogue = useMemo<Expertise[]>(() => {
    const byId = new Map((specialties ?? []).flatMap((s) => s.expertises).map((e) => [e.id, e]));
    return Array.from(byId.values());
  }, [specialties]);

  const specialtyOptions = useMemo(
    () =>
      (specialties ?? [])
        .filter((s) => s.is_active || specialtyIds.includes(s.id))
        .map((s) => ({ value: String(s.id), label: s.name })),
    [specialties, specialtyIds]
  );

  // Offered = the cascade, plus anything already carried so a deactivated value stays visible
  // and survives a re-save.
  const expertiseOptions = useMemo(() => {
    const byId = new Map((cascade ?? []).map((e) => [e.id, e]));
    for (const id of expertiseIds) {
      const carried = catalogue.find((e) => e.id === id);
      if (carried && !byId.has(id)) byId.set(id, carried);
    }
    return Array.from(byId.values()).map((e) => ({ value: String(e.id), label: e.name }));
  }, [cascade, expertiseIds, catalogue]);

  /**
   * Dropping a specialty drops the expertises that only hung under it — visibly, in the same
   * gesture. Sending an expertise whose specialty is gone would attach a skill reachable from
   * nothing. An expertise the catalogue does not know is kept rather than silently deleted.
   */
  const pickSpecialties = (values: string[]) => {
    const nextSpecialties = values.map(Number);
    const kept = expertiseIds.filter((id) => {
      const expertise = catalogue.find((e) => e.id === id);
      return !expertise || expertise.specialty_ids.some((sid) => nextSpecialties.includes(sid));
    });
    onChange({ specialtyIds: nextSpecialties, expertiseIds: kept });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Layers className="h-4 w-4" aria-hidden="true" />
          {t('Specialties and expertises')}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>
            {t('Specialties')}
            {creating && <span className="text-rose-500"> *</span>}
          </Label>
          <MultiSelect
            id={controlId('specialtyIds')}
            aria-label={t('Specialties')}
            aria-describedby={errors.specialtyIds ? errorId('specialtyIds') : undefined}
            options={specialtyOptions}
            selected={specialtyIds.map(String)}
            onChange={pickSpecialties}
            disabled={readOnly}
            placeholder={specialtyOptions.length === 0 ? t('No active specialty yet') : t('Select...')}
          />
          {/* The same sentence twice over: a refusal on a creation, a standing hint on a record
              written before the referentials — which must stay saveable. */}
          {(errors.specialtyIds || (!readOnly && specialtyIds.length === 0)) && (
            <p
              id={errorId('specialtyIds')}
              className={
                errors.specialtyIds
                  ? 'text-xs text-rose-600 dark:text-rose-400'
                  : 'text-xs text-zinc-500 dark:text-zinc-400'
              }
            >
              {validationMessage(t, 'SPECIALTIES_REQUIRED')}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>{t('Expertises')}</Label>
          <MultiSelect
            aria-label={t('Expertises')}
            options={expertiseOptions}
            selected={expertiseIds.map(String)}
            onChange={(values) => onChange({ expertiseIds: values.map(Number) })}
            disabled={readOnly || specialtyIds.length === 0}
            placeholder={
              specialtyIds.length === 0 ? t('Pick a specialty first') : t('Select...')
            }
          />
          <p className="text-xs text-muted-foreground">
            {t('Expertises follow the specialties selected. Removing a specialty removes the expertises that only hung under it.')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SkillsSection;
